export type HttpClientOptions = RequestInit & {
  searchParams?: ConstructorParameters<typeof URLSearchParams>[0];
  timeout?: number;
};

export type HttpClient = ReturnType<typeof createClient>;

export class HttpError extends Error {
  constructor(response: Response) {
    const code =
      response.status || response.status === 0 ? response.status : '';
    const title = response.statusText || '';
    const status = `${code} ${title}`.trim();
    const reason = status ? `status code ${status}` : 'an unknown error';

    super(`Request failed with ${reason}`);
  }
}

const getSignal = (timeout?: number) => {
  if (!timeout) {
    return {
      signal: undefined,
      clear: () => {
        // noop
      },
    };
  }

  const controller = new AbortController();

  const timer = setTimeout(() => {
    controller.abort();
  }, timeout);

  const clear = () => {
    clearTimeout(timer);
  };

  return {
    signal: controller.signal,
    clear,
  };
};

export const createClient = ({
  baseUrl,
  timeout: defaultTimeout = 1500,
}: {
  baseUrl: string;
  timeout?: number;
}) => {
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '');

  return async (url: string, options: HttpClientOptions = {}) => {
    const { searchParams, timeout = defaultTimeout, ...init } = options;

    const controller = getSignal(timeout);

    let input = `${normalizedBaseUrl}${url}`;

    if (searchParams) {
      const params = new URLSearchParams(searchParams).toString();

      input += `?${params}`;
    }

    const response = await window.fetch(input, {
      ...init,
      signal: controller.signal,
    });

    controller.clear();

    if (!response.ok) {
      throw new HttpError(response);
    }

    return response;
  };
};
